<template>
  <div>
    <hr />
    <b-table
      :data="simuls.length ? simuls : []"
      :hoverable="true"
      :focusable="true"
      style="width: 100%"
    >
      <b-table-column
        field="name"
        label="Name"
        cell-class="has-text-left"
        v-slot="{ row: simul }"
      >
        <router-link :to="`/simuls/${simul.id}`" class="has-text-info mr-2">
          <strong>
            {{
              simul.simulName ? simul.simulName : `${simul.owner.name}'s simul`
            }}
            <i v-if="simul.minRating || simul.maxRating">
              | Rating [{{ simul.minRating }}-{{ simul.maxRating }}]
            </i>
          </strong>
        </router-link>
        <b-tag v-if="isJoined(simul)" type="is-success">joined</b-tag>
      </b-table-column>

      <b-table-column
        field="host"
        label="Host"
        cell-class="has-text-left"
        v-slot="{ row: simul }"
      >
        <ProfileCard :player="simul.owner"></ProfileCard>
      </b-table-column>

      <b-table-column
        field="type"
        label="Type"
        centered
        v-slot="{ row: simul }"
      >
        <p>
          {{ simul | timeText }}
        </p>
        <b-tag :type="simul.privacy === 'Public' ? 'is-info' : 'is-danger'">
          {{ simul | simulPrivacyText }}
        </b-tag>
      </b-table-column>

      <b-table-column
        field="startTime"
        label="Start Time"
        centered
        v-slot="{ row: simul }"
      >
        <p>{{ simul.createdAt | asDate }}</p>
        <p>{{ simul.createdAt | asDayjsFormat("hh:mm A (UTCZ)") }}</p>
      </b-table-column>

      <b-table-column
        field="players"
        label="Players"
        centered
        v-slot="{ row: simul }"
      >
        <p>
          <span>
            {{ simul.players ? simul.players.length : 0 }}
          </span>
          <span v-if="simul.playerLimit">/ {{ simul.playerLimit }}</span>
        </p>
        <p v-if="isJoinable(simul)">
          <button
            v-if="!isMine(simul) && !isStarted(simul)"
            class="button is-small"
            :class="
              isRequested(simul)
                ? 'is-secondary'
                : isJoined(simul)
                ? 'is-danger'
                : 'is-primary'
            "
            @click="
              isRequested(simul)
                ? null
                : isJoined(simul)
                ? handleLeave(simul)
                : handleJoin(simul)
            "
          >
            {{
              isRequested(simul)
                ? "Requested"
                : isJoined(simul)
                ? "Leave"
                : "Join"
            }}
          </button>
        </p>
      </b-table-column>

      <b-table-column
        field="status"
        label="Status"
        centered
        v-slot="{ row: simul }"
      >
        {{ simul | simulStatusText }}
      </b-table-column>

      <template #empty>
        <div class="has-text-left">There is currently no simul</div>
      </template>
    </b-table>
    <hr />
  </div>
</template>

<script>
import { SIMUL_STATUS } from "vue-chess-shared/constants";
import firebase from "firebase/app";
import { firestore } from "@/services/firebase";
import { mapGetters } from "vuex";
import {
  ONLINE_STATUS,
  SIMUL_PRIVACY,
  SIMUL_REQUEST_STATUS,
} from "@/constants";
import ProfileCard from "../players/ProfileCard.vue";

export default {
  components: { ProfileCard },
  name: "SimulTable",

  props: ["simuls"],

  data() {
    return {
      simulRequests: [],
    };
  },

  firestore: {
    simulRequests: firestore.collection("simulRequests"),
  },

  computed: {
    ...mapGetters({
      currentPlayer: "getPlayer",
      onlineStatus: "getOnlineStatus",
    }),
  },

  filters: {
    simulStatusText(simul) {
      if (simul.status === SIMUL_STATUS.CREATED) return "Waiting";
      if (simul.status === SIMUL_STATUS.STARTED) return "Live";
      return "Ended";
    },

    simulPrivacyText(simul) {
      switch (simul.privacy) {
        case SIMUL_PRIVACY.PRIVATE:
          return "Private (invite only)";
        case SIMUL_PRIVACY.PUBLIC:
          return "Public";
        default:
          return "";
      }
    },
  },

  methods: {
    isJoinable(simul) {
      return (
        simul.status === SIMUL_STATUS.CREATED ||
        simul.status === SIMUL_STATUS.STARTED
      );
    },

    isStarted(simul) {
      return simul.status === SIMUL_STATUS.STARTED;
    },

    isMine(simul) {
      return simul.owner.user_id === this.currentPlayer.user_id;
    },

    isRequested(simul) {
      return (this.simulRequests || []).find(
        (r) =>
          r.simulId === simul.id &&
          r.player.user_id === this.currentPlayer.user_id &&
          r.status === SIMUL_REQUEST_STATUS.REQUESTED
      );
    },

    isJoined(simul) {
      if (!simul.players) return false;
      return !!simul.players.find(
        (p) => p.user_id === this.currentPlayer.user_id
      );
    },

    async handleJoin(simul) {
      if (this.onlineStatus === ONLINE_STATUS.PLAYING) {
        this.$buefy.toast.open({
          message: "Cannot join a simul while in a tournament!",
          type: "is-warning",
        });
        return;
      }

      if (simul.privacy !== SIMUL_PRIVACY.PUBLIC) {
        this.$buefy.toast.open({
          message: "This is an invite only simul",
          type: "is-warning",
        });
        return;
      }

      const userRating = this.currentPlayer.stats
        ? this.currentPlayer.stats.rating || 0
        : 0;
      const minRating = simul.minRating || 0;
      const maxRating = simul.maxRating || 99999;

      if (userRating < minRating || userRating > maxRating) {
        this.$buefy.toast.open({
          message: "Your rating is not eligible to join this simul",
          type: "is-warning",
        });
        return;
      }

      if (simul.status === SIMUL_STATUS.FINISHED) {
        return;
      }

      if (simul.status === SIMUL_STATUS.STARTED) {
        this.requestJoin(simul);
        return;
      }

      await firestore
        .collection("simuls")
        .doc(simul.id)
        .update({
          players: firebase.firestore.FieldValue.arrayUnion(this.currentPlayer),
        });

      if (this.$route.name === "simul") {
        return;
      }

      this.$router.push({ name: "simul", params: { simulId: simul.id } });
    },

    async handleLeave(simul) {
      await firestore
        .collection("simuls")
        .doc(simul.id)
        .update({
          players: firebase.firestore.FieldValue.arrayRemove(
            this.currentPlayer
          ),
        });

      this.$router.push({ name: "simuls" });
    },

    async requestJoin(simul) {
      if (this.isRequested(simul)) return;

      await firestore.collection("simulRequests").add({
        simulId: simul.id,
        player: this.currentPlayer,
        status: SIMUL_REQUEST_STATUS.REQUESTED,
      });

      this.$buefy.toast.open({
        message: "Request sent! You will join the simul once the host approved",
        type: "is-info",
      });
    },
  },
};
</script>

<style scoped>
hr {
  background-color: #d7d7d7;
  margin-top: 0px;
  margin-bottom: 0;
}
</style>
